import {
  Component,
  HostBinding,
  Input,
  Output,
  OnInit,
  OnChanges,
  EventEmitter,
  ChangeDetectionStrategy,
  ChangeDetectorRef
} from '@angular/core'

import { Dimension, Metric } from '../../../models/analytics-api/schema.model';
import { Result } from '../../../models/result.model';

import { ChartOptions } from '../chart-options'
import { DEFAULT_OPTIONS, DEFAULT_STYLE_OPTIONS } from '../default-chart-options'

import { ColorService } from '../../../services/color/color.service';

const SUPPORTED_INPUTS = [{
  bindTo: 'dimensions',
  label: 'Dimension',
  type: 'dimension'
}, {
  bindTo: 'metrics',
  label: 'Metrics',
  type: 'metric'
}]

const HIGHCHARTS_OPTIONS = {
  chart: {
    type: 'line'
  },
  plotOptions: {
    series: {
      threshold: 0,
      softThreshold: true,
    }
  }
}

export interface LineChartOptions extends ChartOptions {
  spline?: boolean
}

@Component({
  selector: 'line-chart',
  template: '<chart *ngIf="series" [series]="series" [highchartsOptions]="highchartsOptions" [chartOptions]="chartOptions"></chart>',
  styles: [`
    :host {
      height: 100%;
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class LineChart implements OnChanges {
  @HostBinding('style.backgroundColor') backgroundColor: string

  @Input() result: Result
  @Input() dimensions: Dimension[]
  @Input() metrics: Metric[]
  @Input() style: any = {}
  @Input() chartOptions: LineChartOptions = {}

  static supportedInputs = SUPPORTED_INPUTS
  static supportedOptions = [{
    bindTo: 'rollup',
    label: 'Sum by',
    type: 'rollupDropdown',
  }, ...DEFAULT_OPTIONS]
  static supportedStyle = DEFAULT_STYLE_OPTIONS
  highchartsOptions: any
  series: any[]

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private colorService: ColorService
  ) {
    this.highchartsOptions = HIGHCHARTS_OPTIONS
  }

  ngOnChanges() {
    if (this.result && this.dimensions && this.metrics) {
      const dimensionIndex = this.result.dimensions.findIndex(d => d.name === this.dimensions.at(-1).name)
      this.series = this.result.series.map(ss => {
        const slice = ss.slices[dimensionIndex]
        return ss.metrics.map(metric => {
          const data = this.result.timestamps.map((t, i) => [t, metric.values[i]])
          let nameParts = []
          if (this.result.dimensions.length > 1 || ss.metrics.length == 1) {
            nameParts.push(slice.value)
          }
          if (ss.metrics.length > 1) {
            nameParts.push(metric.displayName)
          }
          return {
            name: nameParts.join(' - '),
            data: data,
            format: metric.format
          }
        })
      }).flat()
      if (!this.chartOptions) {
        this.chartOptions = {}
      }

      if (this.style) {
        if (this.style.baseColor) {
          this.chartOptions.colors = this.colorService.getSimilarColorSeries(this.style.baseColor, this.series.length)
        } else {
          this.chartOptions.colors = this.colorService.defaultColors
        }
        this.chartOptions.textColor = this.style.textColor
        this.chartOptions.backgroundColor = this.style.backgroundColor
      }
      this.chartOptions.showLegend = true
    }
  }
}
